import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD = 5;
const SELECTION_THRESHOLD = 0.1;

const useSelectionBox = ({
  containerRef,
  onSelect,
  autoClearOnClick = false,
}) => {
  const dragStart = useRef(null);
  const [boxStyles, setBoxStyles] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedByTable, setSelectedByTable] = useState({});
  const [selectedCount, setSelectedCount] = useState(0);
  const animationFrameId = useRef(null);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectedByTable({});
    setSelectedCount(0);
    setBoxStyles(null);
    if (onSelect) onSelect([], {});
  }, [onSelect]);

  const calculateSelection = (box) => {
    if (!box || !containerRef.current) return;

    const selectionRect = {
      left: box.left,
      right: box.left + box.width,
      top: box.top,
      bottom: box.top + box.height,
    };

    const selected = [];
    const grouped = {};

    const playerElements =
      containerRef.current.querySelectorAll("[data-player-id]");

    playerElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const parentRect = el.offsetParent?.getBoundingClientRect();
      if (!parentRect) return;

      const isVisible =
        rect.bottom >= parentRect.top &&
        rect.top <= parentRect.bottom &&
        rect.right >= parentRect.left &&
        rect.left <= parentRect.right;

      if (!isVisible) return;

      // Calculate intersection
      const intersectLeft = Math.max(selectionRect.left, rect.left);
      const intersectRight = Math.min(selectionRect.right, rect.right);
      const intersectTop = Math.max(selectionRect.top, rect.top);
      const intersectBottom = Math.min(selectionRect.bottom, rect.bottom);

      const intersectWidth = Math.max(0, intersectRight - intersectLeft);
      const intersectHeight = Math.max(0, intersectBottom - intersectTop);
      const intersectArea = intersectWidth * intersectHeight;

      const playerArea = rect.width * rect.height;

      if (intersectArea / playerArea >= SELECTION_THRESHOLD) {
        const playerId = el.getAttribute("data-player-id");
        const tableId = el.getAttribute("data-table-id");

        selected.push(playerId);

        if (tableId) {
          if (!grouped[tableId]) grouped[tableId] = [];
          grouped[tableId].push(playerId);
        }
      }
    });

    setSelectedIds(selected);
    setSelectedByTable(grouped);
    setSelectedCount(selected.length);
    if (onSelect) onSelect(selected, grouped);
  };

  const scheduleSelection = (box) => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(() =>
      calculateSelection(box)
    );
  };

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (e.target.closest("[data-no-select-box]")) return;

      if (e.button === 0 && autoClearOnClick && selectedIds.length > 0) {
        clearSelection();
      }
      if (e.button !== 0) return;
      dragStart.current = { x: e.clientX, y: e.clientY };
      setBoxStyles(null);
    };

    const handleMouseMove = (e) => {
      if (!dragStart.current) return;

      const startX = dragStart.current.x;
      const startY = dragStart.current.y;
      const currentX = e.clientX;
      const currentY = e.clientY;

      const dx = Math.abs(currentX - startX);
      const dy = Math.abs(currentY - startY);
      if (dx < DRAG_THRESHOLD && dy < DRAG_THRESHOLD) return;

      const box = {
        left: Math.min(startX, currentX),
        top: Math.min(startY, currentY),
        width: dx,
        height: dy,
      };

      setBoxStyles(box);
      scheduleSelection(box);
    };

    const handleMouseUp = () => {
      if (boxStyles) calculateSelection(boxStyles);
      dragStart.current = null;
      setBoxStyles(null);
    };

    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current);
    };
  }, [
    boxStyles,
    containerRef,
    onSelect,
    selectedIds,
    clearSelection,
    autoClearOnClick,
  ]);

  return {
    boxStyles,
    selectedCount,
    selectedIds,
    selectedByTable,
    clearSelection,
  };
};

export default useSelectionBox;
